/**
 * Lo derivado de un camión del cuadro.
 *
 * Todo lo de aquí se CALCULA, y no porque sea más elegante: la tabla de
 * Dataverse no tiene ninguna columna de estado. Sólo hay las horas del tramo y
 * el `statecode` del registro.
 *
 * La consecuencia hay que tenerla presente: `salido` significa «ya debería haber
 * salido», no «salió». Que un camión se haya ido de verdad es un hecho físico
 * que ningún dato conoce, y si algún día hace falta distinguirlo habrá que
 * añadir la columna antes que la pantalla.
 */
import type { CamionCreateRequest, CamionMuelle, CargaSalida, EstadoCamion } from '@/types'
import { pickingPct } from '@/utils/cargasSalida'

export function msDe(iso?: string | null): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? null : t
}

/** El tramo del camión en milisegundos, o `null` si le falta alguna hora. */
export function tramoDe(c: CamionMuelle): { iniMs: number; finMs: number } | null {
  const ini = msDe(c.fechaInicio)
  const fin = msDe(c.fechaFin)
  if (ini === null || fin === null || fin <= ini) return null
  return { iniMs: ini, finMs: fin }
}

/**
 * Qué se pinta de este camión.
 *
 * `enRiesgo` gana a todo lo demás: es lo único que pide una acción. Después
 * manda el reloj.
 */
export function estadoCamionDe(
  c: CamionMuelle,
  cargas: CargaSalida[],
  ahora = Date.now(),
): EstadoCamion {
  if (!c.activo) return 'anulado'

  const enRiesgo = cargas.some((x) => pickingPct(x) < 100)
  const tramo = tramoDe(c)
  if (!tramo) return enRiesgo ? 'enRiesgo' : 'programado'

  if (ahora >= tramo.finMs) return 'salido'
  if (ahora >= tramo.iniMs) return enRiesgo ? 'enRiesgo' : 'cargando'
  return enRiesgo ? 'enRiesgo' : 'programado'
}

/**
 * Los colores de cada estado, en un solo sitio.
 *
 * La barra de un camión se pinta como un evento de calendario: el color **pleno**
 * de fondo y el texto encima. Es el lenguaje que la gente ya sabe leer en una
 * rejilla de horas, y a diferencia de una tarjeta clara se ve a cualquier ancho.
 *
 * - `solido`  — el fondo de la barra.
 * - `avance`  — la banda del picking dentro de ella, más oscura que el fondo.
 * - `barra`   — el cuadradito de la leyenda y de los filtros.
 * - `chip`    — para cuando el estado va suelto, fuera de una barra.
 *
 * Que la leyenda y las barras salgan del MISMO objeto es lo que impide que
 * acaben diciendo cosas distintas.
 */
export const COLOR_ESTADO: Record<EstadoCamion, {
  solido: string
  avance: string
  barra: string
  texto: string
  chip: string
}> = {
  programado: {
    solido: 'bg-sky-600',     avance: 'bg-sky-800',
    barra: 'bg-sky-500',      texto: 'text-sky-600',
    chip: 'bg-sky-50 text-sky-700 ring-sky-200',
  },
  cargando: {
    solido: 'bg-amber-500',   avance: 'bg-amber-700',
    barra: 'bg-amber-500',    texto: 'text-amber-600',
    chip: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  salido: {
    solido: 'bg-slate-500',   avance: 'bg-slate-700',
    barra: 'bg-slate-400',    texto: 'text-slate-500',
    chip: 'bg-slate-100 text-slate-600 ring-slate-200',
  },
  enRiesgo: {
    solido: 'bg-rose-600',    avance: 'bg-rose-800',
    barra: 'bg-rose-500',     texto: 'text-rose-600',
    chip: 'bg-rose-50 text-rose-700 ring-rose-200',
  },
  anulado: {
    solido: 'bg-slate-400',   avance: 'bg-slate-500',
    barra: 'bg-slate-300',    texto: 'text-slate-400',
    chip: 'bg-slate-50 text-slate-400 ring-slate-200',
  },
}

/** `HH:mm` en hora local. */
export function hhmm(iso?: string | null): string {
  const ms = msDe(iso)
  if (ms === null) return '—'
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Suma de palets **contando los huecos**.
 *
 *  Si NINGÚN camión lo tiene informado el total es `null`, no cero: el palets lo
 *  teclea una persona, y un cero diría que no hay nada que mover. */
export function totalPalets(camiones: CamionMuelle[]): { total: number | null; sinDato: number } {
  let total = 0
  let con = 0
  let sinDato = 0
  for (const c of camiones) {
    if (c.palets === null || c.palets === undefined) sinDato += 1
    else { total += c.palets; con += 1 }
  }
  return { total: con > 0 ? total : null, sinDato }
}

/**
 * El cuerpo que se manda al crear o editar un camión.
 *
 * Existe como función aparte para que el contrato con la API **tenga un test**.
 * Estaba escrito a mano dentro del drawer con los nombres `inicio`/`fin`, y el
 * backend los declara `fechaInicio`/`fechaFin`: el alta devolvía un 422 de campo
 * ausente y TypeScript no podía verlo, porque los dos lados se declaran por
 * separado y nadie los compara.
 *
 * Las horas van en ISO **con zona**. `new Date('2026-09-23T08:00')` se interpreta
 * en hora local y `toISOString()` le pone el offset; mandar la cadena tal cual
 * haría que el backend la tomara por UTC y el camión se movería dos horas.
 *
 * Los campos opcionales vacíos se omiten en vez de mandarse como cadena vacía:
 * una matrícula en blanco no es una matrícula.
 */
export function cuerpoCamion(v: {
  muelle: string
  almacen: string
  inicioLocal: string
  finLocal: string
  matricula?: string
  transportista?: string
  palets?: string
}): CamionCreateRequest | null {
  const ini = new Date(v.inicioLocal)
  const fin = new Date(v.finLocal)
  if (!v.muelle || Number.isNaN(ini.getTime()) || Number.isNaN(fin.getTime())) return null

  const palets = v.palets?.trim() ? Number(v.palets) : undefined
  return {
    muelle: v.muelle,
    almacen: v.almacen,
    fechaInicio: ini.toISOString(),
    fechaFin: fin.toISOString(),
    ...(v.matricula?.trim() ? { matricula: v.matricula.trim() } : {}),
    ...(v.transportista?.trim() ? { transportista: v.transportista.trim() } : {}),
    ...(palets !== undefined && Number.isFinite(palets) ? { palets } : {}),
  }
}


export interface PaletsCamion {
  /** Lo que se pinta. `null` = no hay de dónde sacarlo. */
  total: number | null
  /** `true` si lo tecleó una persona; `false` si es la suma estimada. */
  manual: boolean
  /** Cargas cuyo palets no se pudo estimar: el total es un «al menos». */
  incompleto: number
}

/**
 * Los palets de un camión.
 *
 * Por defecto es la **suma de los palets estimados de sus cargas**, que es lo que
 * pidió el peticionario. La estimación sale del maestro de unidades por palet y
 * **no cubre el 21 % de los artículos**, así que se cuenta cuántas cargas quedan
 * sin estimar: el total es un «al menos», y decirlo es la diferencia entre un
 * dato y una cifra que alguien se cree.
 *
 * Si alguien teclea el campo a mano, gana: el almacén cuenta mejor que nosotros.
 */
export function paletsDeCamion(c: CamionMuelle, cargas: CargaSalida[]): PaletsCamion {
  if (c.palets !== null && c.palets !== undefined) {
    return { total: c.palets, manual: true, incompleto: 0 }
  }
  let suma = 0
  let con = 0
  let incompleto = 0
  for (const carga of cargas) {
    if (carga.palets === null || carga.palets === undefined) incompleto += 1
    else { suma += carga.palets; con += 1 }
    if ((carga.paletsSinMaestro ?? 0) > 0 && carga.palets !== null) incompleto += 0
  }
  return { total: con > 0 ? suma : null, manual: false, incompleto }
}
